/**
 * ChipField Component
 * 
 * A field that displays a value as a styled chip with optional remove action.
 * Used for displaying selected entities like Company, Reseller, Location.
 */

import React from 'react';
import { Box, Typography, Chip, Button, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface ChipFieldProps {
  /** Field label */
  label: string;
  /** Current value, null if unassigned */
  value: string | null;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Callback when assign/change button is clicked */
  onAssign?: () => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Chip color variant */
  color?: 'default' | 'primary' | 'success' | 'warning' | 'error';
  /** Placeholder text when no value */
  placeholder?: string;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const colorMap = {
  default: {
    background: colors.neutral[100],
    text: colors.neutral[700],
    border: colors.neutral[300],
  },
  primary: {
    background: alpha(colors.primary[500], 0.12),
    text: colors.primary[700],
    border: alpha(colors.primary[500], 0.3),
  },
  success: {
    background: colors.success.light,
    text: colors.success.dark,
    border: colors.success.main,
  },
  warning: {
    background: colors.warning.light,
    text: colors.warning.dark,
    border: colors.warning.main,
  },
  error: {
    background: colors.error.light,
    text: colors.error.dark,
    border: colors.error.main,
  },
};

export const ChipField: React.FC<ChipFieldProps> = ({
  label,
  value,
  onRemove,
  onAssign,
  disabled = false,
  color = 'primary',
  placeholder,
  sx,
}) => {
  const colorStyle = colorMap[color];
  const displayPlaceholder = placeholder || `No ${label.toLowerCase()} assigned`;

  return (
    <Box sx={sx}>
      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          mb: 0.75,
          fontWeight: typographyTokens.fontWeight.medium,
          color: colors.neutral[700],
          fontSize: typographyTokens.fontSize.sm,
        }}
      >
        {label}
      </Typography>

      {/* Value Display */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        {value ? (
          <Chip
            label={value}
            onDelete={!disabled ? onRemove : undefined}
            deleteIcon={
              <CloseIcon
                sx={{
                  fontSize: '16px !important',
                }}
              />
            }
            sx={{
              height: 36,
              borderRadius: `${radii.md}px`,
              backgroundColor: colorStyle.background,
              color: colorStyle.text,
              border: `1px solid ${colorStyle.border}`,
              fontWeight: typographyTokens.fontWeight.medium,
              fontSize: typographyTokens.fontSize.sm,
              '& .MuiChip-deleteIcon': {
                color: colorStyle.text,
                marginRight: '4px',
                '&:hover': {
                  color: colorStyle.text,
                  opacity: 0.7,
                },
              },
            }}
          />
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                color: colors.neutral[400],
                fontStyle: 'italic',
                flex: 1,
              }}
            >
              {displayPlaceholder}
            </Typography>
            {!disabled && onAssign && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={onAssign}
                sx={{
                  borderColor: colors.neutral[300],
                  color: colors.neutral[700],
                  fontWeight: typographyTokens.fontWeight.medium,
                  fontSize: typographyTokens.fontSize.sm,
                  textTransform: 'none',
                  height: 32,
                  px: 1.5,
                  '&:hover': {
                    borderColor: colors.neutral[400],
                    backgroundColor: colors.neutral[50],
                  },
                }}
              >
                Assign
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ChipField;

